import { graphql } from 'react-apollo'
import {
  AccountInput,
  ClaimEncounterMutation,
  CompleteEncounterMutation,
  CreateMessageMutation,
  CreateStandardPhraseMutation,
  CreateVideoSessionMutation,
  DeleteStandardPhraseMutation,
  DismissedReasonsQuery,
  DismissEncounterMutation,
  EnableTwoFactorAuthMutation,
  EndShiftMutation,
  MessageInput,
  PatientDependentInput,
  PatientInput,
  PatientProfileInput,
  PatientProfileQuery,
  PatientStreamQuery,
  ProgressNoteInput,
  ProviderProfileInput,
  ProviderProfileQueryQuery,
  ProviderStreamQuery,
  ReleaseEncounterMutation,
  ResendTwoFactorVerificationTokenMutation,
  StandardPhraseInput,
  StandardPhrasesQueryQuery,
  StartShiftMutation,
  TransferEncounterMutation,
  UpdateAccountMutation,
  UpdateDependentProfileMutation,
  UpdatePatientMutation,
  UpdatePatientProfileMutation,
  UpdateProgressNoteMutation,
  UpdateProviderProfileMutation,
  UpdateStandardPhraseMutation,
  VerifyTwoFactorAuthMutation,
} from '../constants/types'
import { getStreamId } from '../lib/selectors'
import { StreamDataItem } from '../graphql/client/schema'

// local
import CURRENT_DEPENDENT_QUERY from '../graphql/client/CurrentDependentQuery.gql'
import GUI_QUERY from '../graphql/client/GuiQuery.gql'
import STREAM_DATA_QUERY from '../graphql/client/StreamDataQuery.gql'
import UPDATE_CURRENT_DEPENDENT_MUTATION from '../graphql/client/UpdateCurrentDependentMutation.gql'
import UPDATE_GUI_MUTATION from '../graphql/client/UpdateGuiMutation.gql'
import WEB_SOCKET_QUERY from '../graphql/client/WebSocketQuery.gql'

// external shared (2fa stuff not shared now, but could be in the future)
import CREATE_MESSAGE_MUTATION from '../graphql/CreateMessageMutation.gql'
import ENABLE_TWO_FACTOR_AUTH_MUTATION from '../graphql/EnableTwoFactorAuthMutation.gql'
import RESEND_TWO_FACTOR_VERIFICATION_TOKEN_MUTATION from '../graphql/ResendTwoFactorVerificationToken.gql'
import UPDATE_ACCOUNT_MUTATION from '../graphql/UpdateAccountMutation.gql'
import VERIFY_TWO_FACTOR_AUTH_MUTATION from '../graphql/VerifyTwoFactorAuthMutation.gql'

// external patient
import PATIENT_PROFILE_QUERY from '../graphql/patient/PatientProfileQuery.gql'
import PATIENT_STREAM_QUERY from '../graphql/patient/PatientStreamQuery.gql'
import UPDATE_PATIENT_DEPENDENT_MUTATION from '../graphql/patient/UpdatePatientDependentMutation.gql'
import UPDATE_PATIENT_PROFILE_MUTATION from '../graphql/patient/UpdatePatientProfileMutation.gql'
import UPDATE_SURVEY_MUTATION from '../graphql/patient/UpdateSurveyMutation.gql'

// external provider
import CLAIM_ENCOUNTER_MUTATION from '../graphql/provider/ClaimEncounterMutation.gql'
import COMPLETE_ENCOUNTER_MUTATION from '../graphql/provider/CompleteEncounterMutation.gql'
import CREATE_STANDARD_PHRASE_MUTATION from '../graphql/provider/CreateStandardPhraseMutation.gql'
import CREATE_VIDEO_SESSION_MUTATION from '../graphql/provider/CreateVideoSessionMutation.gql'
import DELETE_STANDARD_PHRASE_MUTATION from '../graphql/provider/DeleteStandardPhraseMutation.gql'
import DISMISS_ENCOUNTER_MUTATION from '../graphql/provider/DismissEncounterMutation.gql'
import DISMISSED_REASONS_QUERY from '../graphql/provider/DismissedReasonsQuery.gql'
import END_SHIFT_MUTATION from '../graphql/provider/EndShiftMutation.gql'
import PROVIDER_PROFILE_QUERY from '../graphql/provider/ProviderProfileQuery.gql'
import PROVIDER_STREAM_QUERY from '../graphql/provider/ProviderStreamQuery.gql'
import RELEASE_ENCOUNTER_MUTATION from '../graphql/provider/ReleaseEncounterMutation.gql'
import STANDARD_PHRASES_QUERY from '../graphql/provider/StandardPhrasesQuery.gql'
import START_SHIFT_MUTATION from '../graphql/provider/StartShiftMutation.gql'
import TRANSFER_ENCOUNTER_MUTATION from '../graphql/provider/TransferEncounterMutation.gql'
import UNASSIGNED_ENCOUNTER_COUNT_QUERY from '../graphql/provider/UnassignedEncounterCountQuery.gql'
import UPDATE_PATIENT_MUTATION from '../graphql/provider/UpdatePatientMutation.gql'
import UPDATE_PROGRESS_NOTE_MUTATION from '../graphql/provider/UpdateProgressNoteMutation.gql'
import UPDATE_PROVIDER_PROFILE_MUTATION from '../graphql/provider/UpdateProviderProfileMutation.gql'
import UPDATE_STANDARD_PHRASE_MUTATION from '../graphql/provider/UpdateStandardPhraseMutation.gql'

export const PAGINATION_LIMIT = 25

const getFlattenedData = (data, prop) => {
  if (!data || !data[prop]) {
    return
  }
  return { ...data[prop] }
}

export const withCreateMessageMutation = graphql<
  CreateMessageMutation,
  MessageInput
  // @ts-ignore
>(CREATE_MESSAGE_MUTATION, {
  alias: 'withCreateMessageMutation',
  name: 'createMessage',
})

export const withCreateStandardPhraseMutation = graphql<
  CreateStandardPhraseMutation,
  StandardPhraseInput
  // @ts-ignore
>(CREATE_STANDARD_PHRASE_MUTATION, {
  alias: 'withCreateStandardPhraseMutation',
  name: 'createStandardPhrase',
})

export const withCreateVideoSessionMutation = graphql<
  CreateVideoSessionMutation,
  any
  // @ts-ignore
>(CREATE_VIDEO_SESSION_MUTATION, {
  alias: 'withCreateVideoSessionMutation',
  name: 'createVideoSession',
})

export const withDeleteStandardPhraseMutation = graphql<
  DeleteStandardPhraseMutation,
  any
  // @ts-ignore
>(DELETE_STANDARD_PHRASE_MUTATION, {
  alias: 'withDeleteStandardPhraseMutation',
  name: 'deleteStandardPhrase',
})

export const withUpdateStandardPhraseMutation = graphql<
  UpdateStandardPhraseMutation,
  any
  // @ts-ignore
>(UPDATE_STANDARD_PHRASE_MUTATION, {
  alias: 'withUpdateStandardPhraseMutation',
  name: 'updateStandardPhrase',
})

export const withCurrentDependentQuery = graphql<any, any>(
  // @ts-ignore
  CURRENT_DEPENDENT_QUERY,
  {
    alias: 'withCurrentDependentQuery',
    props: ({ data }) => getFlattenedData(data, 'currentDependent'),
  },
)

export const withEndShiftMutation = graphql<any, EndShiftMutation>(
  // @ts-ignore
  END_SHIFT_MUTATION,
  {
    alias: 'withEndShiftMutation',
    name: 'endShift',
  },
)

// @ts-lint-ignore
export const withEnableTwoFactorAuthMutation = graphql<
  any,
  EnableTwoFactorAuthMutation
>(
  // @ts-ignore
  ENABLE_TWO_FACTOR_AUTH_MUTATION,
  {
    alias: 'withEnableTwoFactorAuthMutation',
    name: 'enableTwoFactorAuth',
  },
)

export const withVerifyTwoFactorAuthMutation = graphql<
  any,
  VerifyTwoFactorAuthMutation
>(
  // @ts-ignore
  VERIFY_TWO_FACTOR_AUTH_MUTATION,
  {
    alias: 'withVerifyTwoFactorAuthMutation',
    name: 'verifyTwoFactorAuth',
  },
)

export const withResendTwoFactorVerificationTokenMutation = graphql<
  any,
  ResendTwoFactorVerificationTokenMutation
>(
  // @ts-ignore
  RESEND_TWO_FACTOR_VERIFICATION_TOKEN_MUTATION,
  {
    alias: 'withResendTwoFactorVerificationToken',
    name: 'resendTwoFactorVerificationToken',
  },
)

// @ts-ignore
export const withGuiQuery = graphql<any, any>(GUI_QUERY, {
  alias: 'withGuiQuery',
  props: ({ data }) => getFlattenedData(data, 'gui'),
})

export const withClaimEncounterMutation = graphql<any, ClaimEncounterMutation>(
  // @ts-ignore
  CLAIM_ENCOUNTER_MUTATION,
  {
    alias: 'withClaimEncounterMutation',
    name: 'claimEncounter',
  },
)

export const withCompleteEncounterMutation = graphql<
  CompleteEncounterMutation,
  ProgressNoteInput
>(
  // @ts-ignore
  COMPLETE_ENCOUNTER_MUTATION,
  {
    alias: 'withCompleteEncounterMutation',
    name: 'completeEncounter',
  },
)

export const withDismissEncounterMutation = graphql<
  any,
  DismissEncounterMutation
>(
  // @ts-ignore
  DISMISS_ENCOUNTER_MUTATION,
  {
    alias: 'withDismissEncounterMutation',
    name: 'dismissEncounter',
  },
)

export const withDismissedReasonsQuery = graphql<any, DismissedReasonsQuery>(
  // @ts-ignore
  DISMISSED_REASONS_QUERY,
  // @ts-ignore
  {
    alias: 'withDismissedReasonsQuery',
    props: ({ data }) =>
      ({ dismissedReasons: data && data.dismissedReasons } as any),
  },
)

export const withReleaseEncounterMutation = graphql<
  any,
  ReleaseEncounterMutation
>(
  // @ts-ignore
  RELEASE_ENCOUNTER_MUTATION,
  {
    alias: 'withReleaseEncounterMutation',
    name: 'releaseEncounter',
  },
)

export const withStandardPhrasesQuery = graphql<any, StandardPhrasesQueryQuery>(
  // @ts-ignore
  STANDARD_PHRASES_QUERY,
  // @ts-ignore
  {
    alias: 'withStandardPhrasesQuery',
    options: {
      variables: {
        limit: 400,
        page: 1,
        query: '',
      },
    },
    props: ({ data }) => {
      if (!data) {
        return
      }
      const { standardPhrases } = data
      if (!standardPhrases) {
        return {}
      }
      return {
        standardPhrases,
      } as any
    },
  },
)

export const withStartShiftMutation = graphql<any, StartShiftMutation>(
  // @ts-ignore
  START_SHIFT_MUTATION,
  {
    alias: 'withStartShiftMutation',
    name: 'startShift',
  },
)

export const withPatientProfileQuery = graphql<{}, PatientProfileQuery>(
  // @ts-ignore
  PATIENT_PROFILE_QUERY,
  // @ts-ignore
  {
    alias: 'withPatientProfileQuery',
    props: ({ data }) => {
      if (!data) {
        return
      }
      const { loading, patientProfile, refetch } = data
      if (!patientProfile) {
        return { loading, refetch }
      }
      return {
        ...patientProfile,
        loading,
        refetch,
      } as any
    },
  },
)

export const withProviderProfileQuery = graphql<{}, ProviderProfileQueryQuery>(
  // @ts-ignore
  PROVIDER_PROFILE_QUERY,
  // @ts-ignore
  {
    alias: 'withProviderProfileQuery',
    props: ({ data }) => {
      if (!data) {
        return
      }
      const { loading, providerProfile, refetch } = data
      if (!providerProfile) {
        return { loading, refetch }
      }
      return {
        ...providerProfile,
        loading,
        refetch,
      } as any
    },
  },
)

// @ts-ignore
export const withStreamDataQuery = graphql<any, any>(STREAM_DATA_QUERY, {
  alias: 'withStreamDataQuery',
  props: ({ data, ownProps }) => {
    if (!data || !data.streamData) {
      return
    }
    const streamId = getStreamId(ownProps) || 0
    return {
      ...data.streamData,
      streamDataItem:
        data.streamData.streamDataItems.find(
          (s: StreamDataItem) => s.key === streamId,
        ) || {},
    }
  },
})

export const withPatientStreamQuery = graphql<any, PatientStreamQuery>(
  // @ts-ignore
  PATIENT_STREAM_QUERY,
  // @ts-ignore
  {
    alias: 'withPatientStreamQuery',
    options: props => ({
      variables: {
        streamId: getStreamId(props),
      },
    }),
    props: ({ data }) => {
      if (!data) {
        return
      }
      const { loading, stream } = data
      if (!stream) {
        return { isStreamLoading: loading }
      }
      return {
        ...stream,
        isStreamLoading: loading,
      } as any
    },
  },
)

// @ts-ignore
export const withProviderStreamQuery = graphql<any, ProviderStreamQuery>(
  PROVIDER_STREAM_QUERY,
  // @ts-ignore
  {
    alias: 'withProviderStreamQuery',
    options: props => ({
      variables: {
        streamId: getStreamId(props),
      },
    }),
    props: ({ data }) => {
      if (!data) {
        return
      }
      const { loading, stream } = data
      if (!stream) {
        return { isStreamLoading: loading }
      }
      return {
        ...stream,
        isStreamLoading: loading,
      } as any
    },
  },
)

export const withTransferEncounterMutation = graphql<
  any,
  TransferEncounterMutation
>(
  // @ts-ignore
  TRANSFER_ENCOUNTER_MUTATION,
  {
    alias: 'withTransferEncounterMutation',
    name: 'transferEncounter',
  },
)

export const withUnassignedEncounterCountQuery = graphql<any, any>(
  // @ts-ignore
  UNASSIGNED_ENCOUNTER_COUNT_QUERY,
  // @ts-ignore
  {
    alias: 'withUnassignedEncounterCountQuery',
    props: ({ data }) =>
      ({
        unassignedEncounterCount: data && data.unassignedEncounterCount,
      } as any),
  },
)

export const withUpdateAccountMutation = graphql<
  UpdateAccountMutation,
  AccountInput
  // @ts-ignore
>(UPDATE_ACCOUNT_MUTATION, {
  alias: 'withUpdateAccountMutation',
  name: 'updateAccount',
})

export const withUpdateCurrentDependentMutation = graphql<any, any>(
  // @ts-ignore
  UPDATE_CURRENT_DEPENDENT_MUTATION,
  {
    alias: 'withUpdateCurrentDependentMutation',
    name: 'updateCurrentDependent',
  },
)

// @ts-ignore
export const withUpdateGuiMutation = graphql<any, any>(UPDATE_GUI_MUTATION, {
  alias: 'withUpdateGuiMutation',
  name: 'updateGui',
})

export const withUpdatePatientDependentMutation = graphql<
  UpdateDependentProfileMutation,
  PatientDependentInput
>(
  // @ts-ignore
  UPDATE_PATIENT_DEPENDENT_MUTATION,
  {
    alias: 'withUpdatePatientDependentMutation',
    name: 'updatePatientDependent',
  },
)

export const withUpdatePatientMutation = graphql<
  UpdatePatientMutation,
  PatientInput
>(
  // @ts-ignore
  UPDATE_PATIENT_MUTATION,
  {
    alias: 'withUpdatePatientMutation',
    name: 'updatePatient',
  },
)

export const withUpdatePatientProfileMutation = graphql<
  UpdatePatientProfileMutation,
  PatientProfileInput
>(
  // @ts-ignore
  UPDATE_PATIENT_PROFILE_MUTATION,
  {
    alias: 'withUpdatePatientProfileMutation',
    name: 'updatePatientProfile',
  },
)

export const withUpdateProgressNoteMutation = graphql<
  UpdateProgressNoteMutation,
  ProgressNoteInput
>(
  // @ts-ignore
  UPDATE_PROGRESS_NOTE_MUTATION,
  {
    name: 'updateProgressNote',
  },
)

export const withUpdateProviderProfileMutation = graphql<
  UpdateProviderProfileMutation,
  ProviderProfileInput
>(
  // @ts-ignore
  UPDATE_PROVIDER_PROFILE_MUTATION,
  {
    alias: 'withUpdateProviderProfileMutation',
    name: 'updateProviderProfile',
  },
)

// @ts-ignore
export const withWebSocketQuery = graphql<any, any>(WEB_SOCKET_QUERY, {
  alias: 'withWebSocketQuery',
  props: ({ data }) => getFlattenedData(data, 'webSocket'),
})

export const withUpdateSurveyMutation = graphql<any, any>(
  // @ts-ignore
  UPDATE_SURVEY_MUTATION,
  {
    alias: 'withUpdateSurveyMutation',
    name: 'updateSurvey',
  },
)
